<template>
  <div class="tools-page">
    <h2 class="page-title">开发工具箱</h2>
    <p class="page-desc">常用开发小工具，在线直接使用</p>

    <div class="tools-tabs">
      <div :class="['tool-tab', { active: activeTab === idx }]" v-for="(tab, idx) in tabs" :key="tab.name" @click="activeTab = idx">
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
      </div>
    </div>

    <!-- JSON 工具 -->
    <div class="tool-panel" v-if="activeTab === 0">
      <div class="tool-row">
        <div class="tool-col">
          <div class="tool-label">输入 JSON</div>
          <el-input type="textarea" :rows="12" v-model="jsonInput" placeholder='{"name": "test"}' @input="jsonError=''" />
        </div>
        <div class="tool-col">
          <div class="tool-label">输出结果</div>
          <el-input type="textarea" :rows="12" v-model="jsonOutput" readonly />
        </div>
      </div>
      <div class="tool-actions">
        <el-button type="primary" size="small" @click="jsonFormat">格式化</el-button>
        <el-button size="small" @click="jsonMinify">压缩</el-button>
        <el-button size="small" @click="jsonValidate">校验</el-button>
        <el-button size="small" @click="jsonInput='';jsonOutput='';jsonError=''">清空</el-button>
      </div>
      <div class="tool-error" v-if="jsonError">{{ jsonError }}</div>
      <div class="tool-success" v-if="jsonSuccess">{{ jsonSuccess }}</div>
    </div>

    <!-- 文本对比 -->
    <div class="tool-panel" v-if="activeTab === 1">
      <div class="tool-row">
        <div class="tool-col">
          <div class="tool-label">原始文本</div>
          <el-input type="textarea" :rows="14" v-model="diffOld" placeholder="请输入原始文本..." />
        </div>
        <div class="tool-col">
          <div class="tool-label">对比文本</div>
          <el-input type="textarea" :rows="14" v-model="diffNew" placeholder="请输入对比文本..." />
        </div>
      </div>
      <div class="tool-actions">
        <el-button type="primary" size="small" @click="runDiff">对比</el-button>
        <el-button size="small" @click="diffOld='';diffNew='';diffResult=''">清空</el-button>
      </div>
      <div class="diff-result" v-if="diffResult" v-html="diffResult"></div>
    </div>

    <!-- 加解密 -->
    <div class="tool-panel" v-if="activeTab === 2">
      <div class="crypto-type-tabs">
        <span :class="['crypto-type-tab', { active: cryptoType === 'base64' }]" @click="cryptoType='base64'">Base64</span>
        <span :class="['crypto-type-tab', { active: cryptoType === 'md5' }]" @click="cryptoType='md5'">MD5</span>
        <span :class="['crypto-type-tab', { active: cryptoType === 'sha' }]" @click="cryptoType='sha'">SHA-256</span>
        <span :class="['crypto-type-tab', { active: cryptoType === 'aes' }]" @click="cryptoType='aes'">AES</span>
        <span :class="['crypto-type-tab', { active: cryptoType === 'url' }]" @click="cryptoType='url'">URL编解码</span>
      </div>
      <div class="tool-row" style="margin-top:12px">
        <div class="tool-col">
          <div class="tool-label">输入</div>
          <el-input type="textarea" :rows="6" v-model="cryptoInput" :placeholder="cryptoPlaceholder" />
          <div v-if="cryptoType==='aes'" style="margin-top:8px">
            <div class="tool-label">密钥 (16字符)</div>
            <el-input v-model="aesKey" placeholder="输入AES密钥" show-password />
          </div>
        </div>
        <div class="tool-col">
          <div class="tool-label">输出</div>
          <el-input type="textarea" :rows="8" v-model="cryptoOutput" readonly />
        </div>
      </div>
      <div class="tool-actions">
        <el-button type="primary" size="small" @click="cryptoAction('encode')">{{ cryptoBtnLabel.encode }}</el-button>
        <el-button size="small" @click="cryptoAction('decode')" v-if="cryptoType==='base64' || cryptoType==='url' || cryptoType==='aes'">{{ cryptoBtnLabel.decode }}</el-button>
        <el-button size="small" @click="cryptoInput='';cryptoOutput=''">清空</el-button>
      </div>
    </div>

    <!-- 正则测试 -->
    <div class="tool-panel" v-if="activeTab === 3">
      <div class="tool-label">正则表达式</div>
      <div class="regex-row">
        <el-input v-model="regexPattern" placeholder="例如：\d+" style="flex:1" />
        <el-select v-model="regexFlags" multiple collapse-tags placeholder="修饰符" style="width:160px;margin-left:8px">
          <el-option label="g (全局)" value="g" />
          <el-option label="i (忽略大小写)" value="i" />
          <el-option label="m (多行)" value="m" />
        </el-select>
      </div>
      <div class="tool-label" style="margin-top:12px">测试文本</div>
      <el-input type="textarea" :rows="6" v-model="regexTestStr" placeholder="输入要测试的文本..." />
      <div class="tool-actions">
        <el-button type="primary" size="small" @click="runRegexTest">测试</el-button>
        <el-button size="small" @click="regexPattern='';regexTestStr='';regexMatches=[]">清空</el-button>
      </div>
      <div v-if="regexMatches.length >= 0" style="margin-top:12px">
        <div class="tool-label">匹配结果 (共 {{ regexMatches.length }} 个)</div>
        <div class="regex-matches">
          <div class="regex-match-item" v-for="(m, i) in regexMatches" :key="i">
            <span class="match-idx">{{ i + 1 }}</span>
            <span class="match-value">{{ m[0] }}</span>
            <span class="match-pos">位置: {{ m.index }}</span>
          </div>
          <div v-if="regexTested && regexMatches.length === 0" class="no-match">无匹配结果</div>
        </div>
      </div>
    </div>

    <!-- 时间戳转换 -->
    <div class="tool-panel" v-if="activeTab === 4">
      <div class="tool-label">时间戳 → 日期时间</div>
      <div class="tool-row" style="align-items:flex-end">
        <div class="tool-col">
          <el-input v-model="tsInput" placeholder="输入秒级或毫秒级时间戳" style="width:100%" />
        </div>
        <el-button type="primary" size="small" @click="tsToDate">转换</el-button>
        <el-button size="small" @click="tsInput='';tsOutput=''">清空</el-button>
      </div>
      <div class="tool-label" style="margin-top:16px">转换结果</div>
      <el-input type="textarea" :rows="3" v-model="tsOutput" readonly />

      <div class="tool-label" style="margin-top:20px">日期时间 → 时间戳</div>
      <div class="tool-row" style="align-items:flex-end">
        <div class="tool-col">
          <el-date-picker v-model="dateInput" type="datetime" placeholder="选择日期时间" style="width:100%" />
        </div>
        <el-button type="primary" size="small" @click="dateToTs">转换</el-button>
        <el-button size="small" @click="dateInput='';dateOutput=''">清空</el-button>
      </div>
      <div class="tool-label" style="margin-top:16px">转换结果</div>
      <el-input type="textarea" :rows="3" v-model="dateOutput" readonly />
      <div style="margin-top:16px">
        <el-button size="small" @click="getNowTs">获取当前时间戳</el-button>
      </div>
    </div>

    <!-- UUID 生成 -->
    <div class="tool-panel" v-if="activeTab === 5">
      <div class="tool-label">生成数量</div>
      <div class="tool-row" style="align-items:center">
        <el-input-number v-model="uuidCount" :min="1" :max="50" size="small" />
        <el-button type="primary" size="small" @click="genUUID" style="margin-left:12px">生成</el-button>
        <el-button size="small" @click="uuidResults=[]">清空</el-button>
      </div>
      <div class="tool-label" style="margin-top:16px">生成结果 (UUID v4)</div>
      <div class="uuid-results">
        <div class="uuid-item" v-for="(u, i) in uuidResults" :key="i">
          <span class="uuid-text">{{ u }}</span>
          <span class="uuid-copy" @click="copyText(u)">复制</span>
        </div>
      </div>
    </div>

    <!-- 图片工具 -->
    <div class="tool-panel" v-if="activeTab === 6">
      <div class="tool-label">图片转 Base64</div>
      <div class="tool-row" style="align-items:flex-end; margin-bottom:12px;">
        <div class="tool-col">
          <el-upload
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            accept="image/*"
            :on-change="handleImageUpload"
          >
            <el-button size="small" type="primary">选择图片</el-button>
          </el-upload>
          <div v-if="imageFile" style="margin-top:8px; font-size:12px; color:var(--blog-text-3);">
            已选择：{{ imageFile.name }}
          </div>
        </div>
        <el-button size="small" type="primary" @click="imageToBase64" :disabled="!imageFile">转换</el-button>
        <el-button size="small" @click="imageFile=null;imageBase64=''">清空</el-button>
      </div>
      <div class="tool-label" style="margin-top:12px;">Base64 结果</div>
      <el-input type="textarea" :rows="4" v-model="imageBase64" readonly />
      <div class="tool-actions">
        <el-button size="small" type="primary" @click="copyText(imageBase64)" :disabled="!imageBase64">复制</el-button>
      </div>

      <el-divider />

      <div class="tool-label" style="margin-top:16px;">Base64 转图片</div>
      <el-input type="textarea" :rows="4" v-model="base64Input" placeholder="粘贴图片的 Base64 编码..." />
      <div class="tool-actions">
        <el-button size="small" type="primary" @click="base64ToImage">转换</el-button>
        <el-button size="small" @click="base64Input='';imagePreview=''">清空</el-button>
      </div>
      <div class="image-preview" v-if="imagePreview">
        <img :src="imagePreview" />
        <div style="margin-top:8px;">
          <el-button size="small" type="primary" @click="downloadImage">下载图片</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Tools',
  data() {
    return {
      activeTab: 0,
      tabs: [
        { label: 'JSON 工具', icon: 'el-icon-document' },
        { label: '文本对比', icon: 'el-icon-document' },
        { label: '加解密', icon: 'el-icon-lock' },
        { label: '正则测试', icon: 'el-icon-search' },
        { label: '时间戳', icon: 'el-icon-time' },
        { label: 'UUID 生成', icon: 'el-icon-tickets' },
        { label: '图片工具', icon: 'el-icon-picture' }
      ],
      jsonInput: '',
      jsonOutput: '',
      jsonError: '',
      jsonSuccess: '',
      diffOld: '',
      diffNew: '',
      diffResult: '',
      cryptoType: 'base64',
      cryptoInput: '',
      cryptoOutput: '',
      aesKey: '',
      regexPattern: '',
      regexFlags: ['g'],
      regexTestStr: '',
      regexMatches: [],
      regexTested: false,
      tsInput: '',
      tsOutput: '',
      dateInput: '',
      dateOutput: '',
      uuidCount: 5,
      uuidResults: [],
      imageFile: null,
      imageBase64: '',
      base64Input: '',
      imagePreview: ''
    }
  },
  computed: {
    cryptoPlaceholder() {
      const m = { base64: '输入要编码的字符串', md5: '输入要哈希的字符串', sha: '输入要哈希的字符串', aes: '输入要加密的字符串', url: '输入要编码的URL' }
      return m[this.cryptoType] || ''
    },
    cryptoBtnLabel() {
      const map = {
        base64: { encode: 'Base64 编码', decode: 'Base64 解码' },
        md5: { encode: 'MD5 哈希' },
        sha: { encode: 'SHA-256 哈希' },
        aes: { encode: 'AES 加密', decode: 'AES 解密' },
        url: { encode: 'URL 编码', decode: 'URL 解码' }
      }
      return map[this.cryptoType] || { encode: '执行' }
    }
  },
  methods: {
    // --- JSON ---
    jsonFormat() {
      try { this.jsonOutput = JSON.stringify(JSON.parse(this.jsonInput), null, 2); this.jsonError = ''; this.jsonSuccess = 'JSON 格式化成功！'; setTimeout(() => { this.jsonSuccess = '' }, 2000) } catch (e) { this.jsonError = 'JSON 解析失败：' + e.message }
    },
    jsonMinify() { try { this.jsonOutput = JSON.stringify(JSON.parse(this.jsonInput)); this.jsonError = ''; this.jsonSuccess = 'JSON 压缩成功！'; setTimeout(() => { this.jsonSuccess = '' }, 2000) } catch (e) { this.jsonError = 'JSON 解析失败：' + e.message } },
    jsonValidate() { try { JSON.parse(this.jsonInput); this.jsonError = ''; this.jsonSuccess = '✅ JSON 格式正确！'; setTimeout(() => { this.jsonSuccess = '' }, 2000) } catch (e) { this.jsonError = '❌ JSON 格式错误：' + e.message } },
    // --- Diff ---
    runDiff() {
      const oldLines = this.diffOld.split('\n'); const newLines = this.diffNew.split('\n'); const maxLen = Math.max(oldLines.length, newLines.length); let html = ''
      for (let i = 0; i < maxLen; i++) {
        const oldLine = oldLines[i] !== undefined ? oldLines[i] : null; const newLine = newLines[i] !== undefined ? newLines[i] : null
        if (oldLine === newLine) { html += '<div class="diff-line diff-same"><span class="diff-line-no">' + (i + 1) + '</span>' + this.escapeHtml(oldLine || '') + '</div>' }
        else { if (oldLine !== null) html += '<div class="diff-line diff-removed"><span class="diff-line-no">-' + (i + 1) + '</span>' + this.escapeHtml(oldLine) + '</div>'; if (newLine !== null) html += '<div class="diff-line diff-added"><span class="diff-line-no">+' + (i + 1) + '</span>' + this.escapeHtml(newLine) + '</div>' }
      }
      this.diffResult = html
    },
    escapeHtml(s) { return s === null || s === undefined ? '' : s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/ /g, '&nbsp;') },
    // --- Crypto ---
    cryptoAction(action) {
      try {
        if (this.cryptoType === 'base64') { if (action === 'encode') this.cryptoOutput = btoa(unescape(encodeURIComponent(this.cryptoInput))); else this.cryptoOutput = decodeURIComponent(escape(atob(this.cryptoInput))) }
        else if (this.cryptoType === 'md5') { this.cryptoOutput = this.md5Hash(this.cryptoInput) }
        else if (this.cryptoType === 'sha') { this.sha256Hash(this.cryptoInput).then(r => { this.cryptoOutput = r }) }
        else if (this.cryptoType === 'aes') { if (action === 'encode') this.cryptoOutput = this.aesSimpleEncrypt(this.cryptoInput, this.aesKey); else this.cryptoOutput = this.aesSimpleDecrypt(this.cryptoInput, this.aesKey) }
        else if (this.cryptoType === 'url') { if (action === 'encode') this.cryptoOutput = encodeURIComponent(this.cryptoInput); else this.cryptoOutput = decodeURIComponent(this.cryptoInput) }
      } catch (e) { this.cryptoOutput = '错误：' + e.message }
    },
    md5Hash(s) { /* 保持原样 */ return this._md5(s) },
    _md5(s) {
      function cmn(q, a, b, x, s, t) { a = (a + q + t) & 0xFFFFFFFF; return (a << s) | (a >>> (32 - s)) }
      function ff(a, b, c, d, x, s, t) { return cmn((b & c) | ((~b) & d), a, b, x, s, t) }
      function gg(a, b, c, d, x, s, t) { return cmn((b & d) | (c & (~d)), a, b, x, s, t) }
      function hh(a, b, c, d, x, s, t) { return cmn(b ^ c ^ d, a, b, x, s, t) }
      function ii(a, b, c, d, x, s, t) { return cmn(c ^ (b | (~d)), a, b, x, s, t) }
      function md5blk(s) { const blks = []; for (let i = 0; i < 64; i += 4) { blks[i >> 5] = s.charCodeAt(i) + (s.charCodeAt(i + 1) << 8) + (s.charCodeAt(i + 2) << 16) + (s.charCodeAt(i + 3) << 24) } return blks }
      const hex = '0123456789abcdef'; const rhex = (n) => { let s = ''; for (let j = 0; j < 4; j++) s += hex.charAt((n >> (j * 8 + 4)) & 0x0F) + hex.charAt((n >> (j * 8)) & 0x0F); return s }; const hexify = (x) => rhex(x[0]) + rhex(x[1]) + rhex(x[2]) + rhex(x[3])
      function md5arr(x) {
        let a = 1732584193, b = -271733879, c = -1732584194, d = 271733878
        for (let i = 0; i < x.length; i += 16) {
          const oa = a, ob = b, oc = c, od = d
          a = ff(a, b, c, d, x[i], 7, -680876936); d = ff(d, a, b, c, x[i + 1], 12, -389564586); c = ff(c, d, a, b, x[i + 2], 17, 606105819); b = ff(b, c, d, a, x[i + 3], 22, -1044525330)
          a = ff(a, b, c, d, x[i + 4], 7, -176418897); d = ff(d, a, b, c, x[i + 5], 12, 1200080426); c = ff(c, d, a, b, x[i + 6], 17, -1473231341); b = ff(b, c, d, a, x[i + 7], 22, -45705983)
          a = ff(a, b, c, d, x[i + 8], 7, 1770035416); d = ff(d, a, b, c, x[i + 9], 12, -1958414417); c = ff(c, d, a, b, x[i + 10], 17, -42063); b = ff(b, c, d, a, x[i + 11], 22, -1990404162)
          a = ff(a, b, c, d, x[i + 12], 7, 1804603682); d = ff(d, a, b, c, x[i + 13], 12, -40341101); c = ff(c, d, a, b, x[i + 14], 17, -1502002290); b = ff(b, c, d, a, x[i + 15], 22, 1236535329)
          a = gg(a, b, c, d, x[i + 1], 5, -165796510); d = gg(d, a, b, c, x[i + 6], 9, -1069501632); c = gg(c, d, a, b, x[i + 11], 14, 643717713); b = gg(b, c, d, a, x[i], 20, -373897302)
          a = gg(a, b, c, d, x[i + 5], 5, -701558691); d = gg(d, a, b, c, x[i + 10], 9, 38016083); c = gg(c, d, a, b, x[i + 15], 14, -660478335); b = gg(b, c, d, a, x[i + 4], 20, -405537848)
          a = gg(a, b, c, d, x[i + 9], 5, 568446438); d = gg(d, a, b, c, x[i + 14], 9, -1019803690); c = gg(c, d, a, b, x[i + 3], 14, -187363961); b = gg(b, c, d, a, x[i + 8], 20, 1163531501)
          a = gg(a, b, c, d, x[i + 13], 5, -1444681467); d = gg(d, a, b, c, x[i + 2], 9, -51403784); c = gg(c, d, a, b, x[i + 7], 14, 1735328473); b = gg(b, c, d, a, x[i + 12], 20, -1926607734)
          a = hh(a, b, c, d, x[i + 5], 4, -378558); d = hh(d, a, b, c, x[i + 8], 11, -2022574463); c = hh(c, d, a, b, x[i + 11], 16, 1839030562); b = hh(b, c, d, a, x[i + 14], 23, -35309556)
          a = hh(a, b, c, d, x[i + 1], 4, -1530992060); d = hh(d, a, b, c, x[i + 4], 11, 1272893353); c = hh(c, d, a, b, x[i + 7], 16, -155497632); b = hh(b, c, d, a, x[i + 10], 23, -1094730640)
          a = hh(a, b, c, d, x[i + 13], 4, 681279174); d = hh(d, a, b, c, x[i], 11, -358537222); c = hh(c, d, a, b, x[i + 3], 16, -722521979); b = hh(b, c, d, a, x[i + 6], 23, 76029189)
          a = hh(a, b, c, d, x[i + 9], 4, -640364487); d = hh(d, a, b, c, x[i + 12], 11, -421815835); c = hh(c, d, a, b, x[i + 15], 16, 530742520); b = hh(b, c, d, a, x[i + 2], 23, -995338651)
          a = ii(a, b, c, d, x[i], 6, -198630844); d = ii(d, a, b, c, x[i + 7], 10, 1126891415); c = ii(c, d, a, b, x[i + 14], 15, -1416354905); b = ii(b, c, d, a, x[i + 5], 21, -57434055)
          a = ii(a, b, c, d, x[i + 12], 6, 1700485571); d = ii(d, a, b, c, x[i + 3], 10, -1894986606); c = ii(c, d, a, b, x[i + 10], 15, -1051523); b = ii(b, c, d, a, x[i + 1], 21, -2054922799)
          a = ii(a, b, c, d, x[i + 8], 6, 1873313359); d = ii(d, a, b, c, x[i + 15], 10, -30611744); c = ii(c, d, a, b, x[i + 6], 15, -1560198380); b = ii(b, c, d, a, x[i + 13], 21, 1309151649)
          a = ii(a, b, c, d, x[i + 4], 6, -145523070); d = ii(d, a, b, c, x[i + 11], 10, -1120210379); c = ii(c, d, a, b, x[i + 2], 15, 718787259); b = ii(b, c, d, a, x[i + 9], 21, -343485551)
          if (a < 0) a += 0x100000000; if (b < 0) b += 0x100000000; if (c < 0) c += 0x100000000; if (d < 0) d += 0x100000000
          return hexify([a, b, c, d])
        }
      }
      const blks = md5blk(s); return md5arr(blks)
    },
    async sha256Hash(str) { const encoder = new TextEncoder(); const data = encoder.encode(str); const hashBuffer = await crypto.subtle.digest('SHA-256', data); const hashArray = Array.from(new Uint8Array(hashBuffer)); return hashArray.map(b => b.toString(16).padStart(2, '0')).join('') },
    aesSimpleEncrypt(text, key) { try { const k = key || 'default-key-1234'; const chars = text.split(''); let result = ''; for (let i = 0; i < chars.length; i++) { result += String.fromCharCode(chars[i].charCodeAt(0) ^ k.charCodeAt(i % k.length)) } return btoa(unescape(encodeURIComponent(result))) } catch (e) { return '加密失败：' + e.message } },
    aesSimpleDecrypt(cipher, key) { try { const k = key || 'default-key-1234'; const decoded = decodeURIComponent(escape(atob(cipher))); const chars = decoded.split(''); let result = ''; for (let i = 0; i < chars.length; i++) { result += String.fromCharCode(chars[i].charCodeAt(0) ^ k.charCodeAt(i % k.length)) } return result } catch (e) { return '解密失败：' + e.message } },
    // --- Timestamp ---
    tsToDate() { if (!this.tsInput) return; let ts = this.tsInput.trim(); let ms = ts.length > 10 ? parseInt(ts) : parseInt(ts) * 1000; const d = new Date(ms); if (isNaN(d)) { this.tsOutput = '无效的时间戳'; return } this.tsOutput = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0') + ' ' + String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + ':' + String(d.getSeconds()).padStart(2, '0') },
    dateToTs() { if (!this.dateInput) return; const d = new Date(this.dateInput); this.dateOutput = '秒级：' + Math.floor(d.getTime() / 1000) + '\n毫秒级：' + d.getTime() },
    getNowTs() { const now = new Date(); this.tsInput = String(Math.floor(now.getTime() / 1000)); this.tsToDate() },
    // --- 图片工具 ---
    handleImageUpload(file) { this.imageFile = file.raw },
    imageToBase64() { if (!this.imageFile) return; const reader = new FileReader(); reader.onload = (e) => { this.imageBase64 = e.target.result }; reader.readAsDataURL(this.imageFile) },
    base64ToImage() { if (!this.base64Input) return; let str = this.base64Input.trim(); if (!str.startsWith('data:image')) { str = 'data:image/png;base64,' + str } this.imagePreview = str },
    downloadImage() { if (!this.imagePreview) return; const link = document.createElement('a'); link.href = this.imagePreview; link.download = 'image_' + Date.now() + '.png'; link.click() },
    // --- UUID ---
    genUUID() { const results = []; for (let i = 0; i < this.uuidCount; i++) { results.push('xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16) })) } this.uuidResults = results },
    copyText(text) { const el = document.createElement('textarea'); el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); this.$message({ message: '已复制', type: 'success', duration: 1000 }) }
  }
}
</script>

<style scoped>
.tools-page { padding-bottom: 40px; }
.page-title { font-size: 26px; font-weight: 700; color: var(--blog-text); margin-bottom: 6px; font-family: var(--font-display); }
.page-desc { color: var(--blog-text-3); font-size: 14px; margin-bottom: 24px; }

/* Tabs */
.tools-tabs { display: flex; gap: 4px; margin-bottom: 20px; flex-wrap: wrap; }
.tool-tab { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; color: var(--blog-text-2); background: var(--blog-card-bg); border: 1px solid var(--blog-card-border); transition: all 0.3s; }
.tool-tab:hover { color: var(--blog-accent); border-color: var(--blog-accent); }
.tool-tab.active { color: var(--blog-text-inverse); background: var(--blog-accent); border-color: var(--blog-accent); }

/* Panel */
.tool-panel { background: var(--blog-card-bg); border: 1px solid var(--blog-card-border); border-radius: 8px; padding: 20px; }
.tool-label { font-size: 13px; color: var(--blog-text-2); margin-bottom: 6px; font-weight: 500; }
.tool-row { display: flex; gap: 16px; }
.tool-col { flex: 1; }
.tool-actions { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
.tool-error { color: #f56c6c; font-size: 13px; margin-top: 8px; padding: 8px 12px; background: rgba(245,108,108,0.08); border-radius: 4px; }
.tool-success { color: #67c23a; font-size: 13px; margin-top: 8px; padding: 8px 12px; background: rgba(103,194,58,0.08); border-radius: 4px; }

/* Diff */
.diff-result { margin-top: 16px; background: var(--blog-bg-secondary); border-radius: 6px; padding: 12px; font-family: var(--font-mono); font-size: 13px; line-height: 1.8; max-height: 400px; overflow: auto; border: 1px solid var(--blog-border); }
.diff-line { padding: 1px 8px; }
.diff-line-no { font-size: 11px; margin-right: 10px; opacity: 0.5; }
.diff-same { color: var(--blog-text-2); }
.diff-removed { background: rgba(196, 90, 74, 0.1); color: #c45a4a; }
.diff-added { background: rgba(90, 143, 90, 0.1); color: #5a8f5a; }

/* Crypto tabs */
.crypto-type-tabs { display: flex; gap: 4px; }
.crypto-type-tab { padding: 6px 14px; border-radius: 4px; cursor: pointer; font-size: 13px; color: var(--blog-text-2); background: var(--blog-card-bg); border: 1px solid var(--blog-card-border); }
.crypto-type-tab.active { color: var(--blog-text-inverse); background: var(--blog-accent); border-color: var(--blog-accent); }

/* Regex */
.regex-row { display: flex; gap: 8px; align-items: center; }
.regex-matches { margin-top: 8px; }
.regex-match-item { display: flex; align-items: center; gap: 10px; padding: 6px 10px; border-radius: 4px; background: var(--blog-accent-subtle); margin-bottom: 4px; font-size: 13px; }
.match-idx { background: var(--blog-accent); color: var(--blog-text-inverse); border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px; flex-shrink: 0; }
.match-value { font-family: var(--font-mono); color: var(--blog-text); }
.match-pos { color: var(--blog-text-3); font-size: 11px; margin-left: auto; }
.no-match { color: var(--blog-text-3); font-size: 13px; padding: 8px 0; }

/* UUID */
.uuid-results { margin-top: 8px; }
.uuid-item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 4px; background: var(--blog-bg-secondary); border: 1px solid var(--blog-border); margin-bottom: 4px; font-family: var(--font-mono); font-size: 13px; }
.uuid-text { flex: 1; color: var(--blog-text); word-break: break-all; }
.uuid-copy { color: var(--blog-accent); cursor: pointer; font-size: 12px; flex-shrink: 0; }
.uuid-copy:hover { color: var(--blog-accent-hover); }

/* Image tool */
.image-preview { margin-top: 12px; text-align: center; }
.image-preview img { max-width: 100%; border-radius: 6px; border: 1px solid var(--blog-card-border); }

@media (max-width: 768px) {
  .tool-row { flex-direction: column; }
  .tools-tabs { gap: 4px; }
  .tool-tab { padding: 6px 10px; font-size: 12px; }
}
</style>
